/**
 * BankStatementsPage — moduł Wyciągi (Faza Persistence Foundation).
 *
 * Funkcjonalność:
 *   1. Tab "Wgraj" — upload pliku → POST /preview → tabela podglądu.
 *      Pod podglądem: przycisk "Zapisz import" → POST /imports/save.
 *      Dedup po stronie backendu — UI pokazuje saved_count / duplicate_count.
 *   2. Tab "Historia importów" — GET /imports.
 *   3. Tab "Transakcje" — GET /transactions.
 *
 * BEZ AI. BEZ Agentów. BEZ analizy płatności. BEZ paczek.
 * Filtracja per-user po stronie backendu (session_token + user_id).
 */
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileSpreadsheet, FileText, FileCode2, Loader2, AlertTriangle,
  History, ListOrdered, Trash2, ArrowUp, ArrowDown, ChevronsUpDown,
  ChevronLeft, ChevronRight, Bot, ArrowRight, ChevronDown, ChevronUp,
  Copy, Check, Download, Search, X as XIcon, ArrowUpAZ, ArrowDownAZ,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { usePageContainer } from "@/hooks/usePageContainer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FORMAT_META = {
  mt940: { label: "MT940", icon: FileCode2, className: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  csv:   { label: "CSV",   icon: FileText,       className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  mbank_csv: { label: "mBank CSV", icon: FileText, className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  xlsx:  { label: "XLSX",  icon: FileSpreadsheet, className: "bg-blue-50 text-blue-700 border-blue-200" },
  unknown: { label: "Nieznany", icon: AlertTriangle, className: "bg-amber-50 text-amber-700 border-amber-200" },
};

const formatAmount = (amount, currency) => {
  if (amount === null || amount === undefined) return "—";
  const sign = amount < 0 ? "-" : "";
  const abs = Math.abs(Number(amount));
  const formatted = abs.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${sign}${formatted}${currency ? " " + currency : ""}`;
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pl-PL", { year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// ─── Sub-components ──────────────────────────────────────────────────────────

const UploadTab = ({ onSaved, switchToTransactions }) => {
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pending, setPending] = useState(null);
  // pending = {
  //   preview_token, file_name, source_format, full_count,
  //   page, page_size, total_pages, rows, warnings, diagnostics, metadata
  // }

  const triggerPicker = () => {
    if (fileInputRef.current) fileInputRef.current.value = "";
    fileInputRef.current?.click();
  };

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    setPending(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const previewRes = await axios.post(`${API}/bank-statements/preview`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      const d = previewRes.data;
      if (!d?.preview?.length) {
        if (d?.format === "unknown") toast.error("Nie rozpoznano formatu pliku.");
        else if ((d?.warnings || []).length > 0) toast.error(d.warnings[0]);
        else toast.error("Plik nie zawiera operacji do zapisania.");
        return;
      }
      if (!d.preview_token) {
        toast.error("Backend nie zwrócił preview_token. Skontaktuj się z administratorem.");
        return;
      }
      setPending({
        preview_token: d.preview_token,
        file_name: d.filename,
        source_format: d.format,
        full_count: d.full_count || d.total_count || 0,
        page: 1,
        page_size: d.page_size || 50,
        total_pages: d.total_pages || 1,
        rows: d.preview || [],
        warnings: d.warnings || [],
        diagnostics: d.diagnostics,
        metadata: d.metadata,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Błąd przetwarzania pliku.");
    } finally {
      setLoading(false);
    }
  };

  const loadPage = async (page, page_size = 50) => {
    if (!pending) return;
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/bank-statements/preview/${pending.preview_token}?page=${page}&page_size=${page_size}`,
        { withCredentials: true },
      );
      const d = r.data;
      setPending((p) => ({
        ...p,
        page: d.page,
        page_size: d.page_size,
        total_pages: d.total_pages,
        rows: d.transactions || [],
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Pending wygasł — wgraj plik ponownie.");
      setPending(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!pending) return;
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/bank-statements/imports/save`,
        { preview_token: pending.preview_token },
        { withCredentials: true },
      );
      const data = r.data || {};
      if (data.already_imported) {
        toast.warning("Plik już zaimportowany.", {
          description: `Plik o tej samej nazwie i okresie ${pending.metadata?.period_from || ""} – ${pending.metadata?.period_to || ""} jest już w historii.`,
        });
        setPending(null);
        return;
      }
      toast.success(`Zapisano ${data.saved_count} operacji`, {
        description: pending.diagnostics
          ? `Wiersze: ${pending.diagnostics.total_data_rows} • OK: ${pending.diagnostics.parsed_ok}${pending.diagnostics.parsed_error ? ` • Błędne: ${pending.diagnostics.parsed_error}` : ""}`
          : undefined,
      });
      // Incremental Agent Wyciągów re-run (addytywne, informacyjne).
      const ba = data.agent_rerun?.bank_agent;
      if (ba?.triggered && ba?.mode === "background") {
        // Nowy flow (2026-08): analiza kontrahentów biegnie w tle, żeby zapis
        // wyciągu odpowiadał natychmiast (wcześniej timeout proxy przy wielu kontrahentach).
        if (ba.counterparty_keys?.length) {
          toast.info("Agent Wyciągów analizuje kontrahentów w tle", {
            description: `Kontrahenci: ${ba.counterparty_keys.length} — profile zaktualizują się za chwilę.`,
          });
        }
      } else if (ba?.triggered) {
        const agentParts = [];
        if (ba.analyzed > 0) agentParts.push(`zaktualizowane profile: ${ba.analyzed}`);
        if (ba.cached > 0) agentParts.push(`z cache: ${ba.cached}`);
        if (ba.failed > 0) agentParts.push(`błędy: ${ba.failed}`);
        if (agentParts.length > 0) {
          toast.info(`Agent Wyciągów — ${agentParts.join(", ")}`, {
            description: ba.counterparty_keys?.length
              ? `Kontrahenci: ${ba.counterparty_keys.length}`
              : undefined,
          });
        }
      }
      setPending(null);
      if (typeof onSaved === "function") onSaved();
      if (typeof switchToTransactions === "function") switchToTransactions();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Nie udało się zapisać importu.");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setPending(null);
  };

  return (
    <div className="space-y-4">
      {!pending && (
        <Card>
          <CardContent className="p-6 flex flex-col items-start gap-3">
            <p className="text-sm text-gray-600">Wgraj wyciąg bankowy (CSV, MT940, XLSX).</p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.mt940,.sta,.txt,.xlsx"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              data-testid="bank-upload-input"
            />
            <Button
              onClick={triggerPicker}
              disabled={loading}
              data-testid="bank-upload-btn"
              className="bg-[#0F1F3D] hover:bg-[#1a2e54] text-white"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsowanie pliku…</>) : (<><Upload className="w-4 h-4 mr-2" /> Wybierz plik</>)}
            </Button>
          </CardContent>
        </Card>
      )}

      {pending && (
        <Card data-testid="bank-preview-pending">
          <CardContent className="p-4 space-y-3">
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="text-sm">
                <span className="font-medium text-gray-900">{pending.file_name}</span>
                <span className="text-gray-500 ml-2">· {pending.source_format}</span>
                <span className="ml-3 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200" data-testid="bank-preview-total">
                  {pending.full_count} transakcji
                </span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving} data-testid="bank-preview-cancel">
                  Anuluj
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={saving || loading}
                  data-testid="bank-preview-save"
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Zapisuję…</>) : `Zapisz import (${pending.full_count})`}
                </Button>
              </div>
            </div>

            <PreviewTable rows={pending.rows} totalCount={pending.full_count} />

            {pending.total_pages > 1 && (
              <div className="flex items-center justify-between text-xs text-gray-600" data-testid="bank-preview-pagination">
                <span>
                  Strona <strong>{pending.page}</strong> / {pending.total_pages}
                  <span className="ml-2 text-gray-400">({pending.rows.length} rekordów na stronie · łącznie {pending.full_count})</span>
                </span>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" onClick={() => loadPage(1, pending.page_size)} disabled={pending.page <= 1 || loading} data-testid="bank-preview-page-first">««</Button>
                  <Button variant="outline" size="sm" onClick={() => loadPage(pending.page - 1, pending.page_size)} disabled={pending.page <= 1 || loading} data-testid="bank-preview-page-prev">«</Button>
                  <Button variant="outline" size="sm" onClick={() => loadPage(pending.page + 1, pending.page_size)} disabled={pending.page >= pending.total_pages || loading} data-testid="bank-preview-page-next">»</Button>
                  <Button variant="outline" size="sm" onClick={() => loadPage(pending.total_pages, pending.page_size)} disabled={pending.page >= pending.total_pages || loading} data-testid="bank-preview-page-last">»»</Button>
                  <select
                    value={pending.page_size}
                    onChange={(e) => loadPage(1, Number(e.target.value))}
                    disabled={loading}
                    data-testid="bank-preview-page-size"
                    className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs text-gray-700 ml-2"
                  >
                    {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / strona</option>)}
                  </select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

const PreviewTable = ({ rows, totalCount }) => (
  <div className="rounded-md border border-gray-200">
    <table className="w-full text-sm table-responsive" data-testid="bank-preview-table">
      <thead className="bg-gray-50 text-gray-600">
        <tr>
          <th className="text-left px-3 py-2 font-medium">Data</th>
          <th className="text-right px-3 py-2 font-medium">Kwota</th>
          <th className="text-left px-3 py-2 font-medium">Kontrahent</th>
          <th className="text-left px-3 py-2 font-medium">Rachunek</th>
          <th className="text-left px-3 py-2 font-medium">Tytuł</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((tx, idx) => (
          <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`bank-preview-row-${idx}`}>
            <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{tx.booking_date || "—"}</td>
            <td className={`px-3 py-2 text-right whitespace-nowrap font-mono tabular-nums ${tx.amount < 0 ? "text-red-700" : "text-emerald-700"}`}>{formatAmount(tx.amount, tx.currency)}</td>
            <td className="px-3 py-2 text-gray-800 max-w-[28ch] truncate" title={tx.counterparty_name || ""}>{tx.counterparty_name || "—"}</td>
            <td className="px-3 py-2 text-gray-600 font-mono text-xs max-w-[24ch] truncate" title={tx.counterparty_iban || ""}>{tx.counterparty_iban || "—"}</td>
            <td className="px-3 py-2 text-gray-700 max-w-[36ch] truncate" title={tx.title || ""}>{tx.title || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
    {totalCount > rows.length && (
      <div className="px-3 py-2 text-xs text-gray-500 bg-gray-50 border-t border-gray-100">
        Wyświetlono pierwsze {rows.length} z {totalCount} operacji.
      </div>
    )}
  </div>
);


import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";


const HistoryTab = ({ reloadKey, onChanged }) => {
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);
  const [refetchTick, setRefetchTick] = useState(0);
  const [confirmDelete, setConfirmDelete] = useState(null); // {_id, file_name, operations_count} | null

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/bank-statements/imports`, { withCredentials: true });
        if (!cancelled) setImports(res.data?.imports || []);
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Błąd pobierania historii.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey, refetchTick]);

  // FIX 2026-02-06: `window.confirm` jest blokowany w mobilnym webview
  // (np. Emergent Preview) → DELETE nigdy nie strzelał. Używamy shadcn
  // AlertDialog, który renderuje się w DOM i działa wszędzie.
  const requestDelete = (imp) => setConfirmDelete(imp);

  const handleDeleteConfirmed = async () => {
    const imp = confirmDelete;
    if (!imp) return;
    setConfirmDelete(null);
    setDeletingId(imp._id);
    try {
      const res = await axios.delete(`${API}/bank-statements/imports/${imp._id}`, { withCredentials: true });
      toast.success("Import usunięty", {
        description: `Usunięto ${res.data?.deleted_transactions ?? 0} operacji.`,
      });
      setRefetchTick((t) => t + 1);
      if (typeof onChanged === "function") onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Błąd usuwania importu.");
    } finally {
      setDeletingId(null);
    }
  };

  // PAX zapisuje oryginalny plik importu w kolekcji `bank_statement_import_files`
  // (od 2026-02). Nowe importy → pobierany jest oryginał (bit-perfect).
  // Starsze importy → fallback do CSV wygenerowanego z bazy. Backend robi ten
  // wybór transparentnie w `GET /imports/{id}/download`. Flaga `imp.has_original`
  // sterują tylko labelem przycisku ("Pobierz oryginał" vs "Pobierz CSV z danych").
  //
  // Mobile fix (2026-02): stary flow axios(blob)+URL.createObjectURL()+<a download>
  // pękał na iOS Safari/Chrome Android (user gesture chain zerwany przez `await`,
  // ObjectURL revokowany za szybko, mobile ignoruje `download` attribute na
  // cross-origin). Nowa strategia: ukryty <a href=endpoint> na TEJ SAMEJ ORIGIN
  // (backend jest pod tą samą domeną preview) — przeglądarka natywnie triggeruje
  // download przez Content-Disposition, cookies lecą automatycznie, brak async
  // gap między klikiem usera a nawigacją. Działa na wszystkich mobilnych.
  const handleDownload = (imp) => {
    if (downloadingId) return; // Prosty guard przed double-click.
    setDownloadingId(imp._id);
    try {
      const url = `${API}/bank-statements/imports/${imp._id}/download`;
      const a = document.createElement("a");
      a.href = url;
      a.rel = "noopener";
      // Nie ustawiamy a.download — backend już wysyła Content-Disposition
      // z poprawną nazwą (oryginalną dla nowych importów, pax_wyciag_*.csv
      // dla starych). Mobile respektuje Content-Disposition, ignoruje download attr.
      document.body.appendChild(a);
      a.click();
      a.remove();

      toast.success("Pobieranie rozpoczęte", {
        description: imp.has_original
          ? "Pobierany jest oryginalny plik wyciągu."
          : "PAX nie ma oryginału — pobierany CSV z danych z bazy.",
      });
    } catch (e) {
      toast.error("Nie udało się rozpocząć pobierania.");
    } finally {
      // Krótki lockout żeby uniknąć double-clicka; sam download idzie asynchronicznie
      // w tle przeglądarki, nie kontrolujemy tego callback'iem.
      setTimeout(() => setDownloadingId(null), 800);
    }
  };

  if (loading) return <div className="text-sm text-gray-500"><Loader2 className="w-4 h-4 inline animate-spin mr-2" />Ładowanie historii…</div>;
  if (!imports.length) return <Card className="border border-gray-200"><CardContent className="p-6 text-sm text-gray-500" data-testid="bank-history-empty">Brak zapisanych importów.</CardContent></Card>;

  return (
    <>
      {/* MOBILE: karty (md:hidden). Każdy import = pełna karta z wszystkimi
          danymi bez ucinania i dwoma przyciskami-akcjami. */}
      <div className="md:hidden space-y-3" data-testid="bank-history-mobile-list">
        {imports.map((imp) => {
          const meta = FORMAT_META[imp.source_format] || FORMAT_META.unknown;
          const isDownloading = downloadingId === imp._id;
          const isDeleting = deletingId === imp._id;
          return (
            <Card
              key={imp._id}
              className="border border-gray-200"
              data-testid={`bank-history-mobile-card-${imp._id}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div
                      className="text-sm font-medium text-gray-900 break-words"
                      data-testid={`bank-history-mobile-file-${imp._id}`}
                    >
                      {imp.file_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {formatDateTime(imp.created_at)}
                    </div>
                  </div>
                  <span
                    className={`shrink-0 inline-flex px-2 py-0.5 rounded text-xs font-medium border ${meta.className}`}
                  >
                    {meta.label}
                  </span>
                </div>

                <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-sm">
                  <dt className="text-gray-500">Operacje:</dt>
                  <dd className="text-gray-900 font-medium">{imp.operations_count}</dd>

                  <dt className="text-gray-500">Zakres:</dt>
                  <dd className="text-gray-900 break-words">
                    {imp.date_from || "—"}
                    {imp.date_to ? ` → ${imp.date_to}` : ""}
                  </dd>

                  <dt className="text-gray-500">Status:</dt>
                  <dd className="text-gray-900">{imp.status || "—"}</dd>
                </dl>

                <div className="flex flex-col gap-2 pt-2 border-t border-gray-100">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => handleDownload(imp)}
                    disabled={isDownloading}
                    data-testid={`bank-history-mobile-download-${imp._id}`}
                    className="w-full justify-center"
                  >
                    {isDownloading ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Pobieranie…</>
                    ) : (
                      <><Download className="w-4 h-4 mr-2" />{imp.has_original ? "Pobierz oryginał" : "Pobierz CSV z danych"}</>
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => requestDelete(imp)}
                    disabled={isDeleting}
                    data-testid={`bank-history-mobile-delete-${imp._id}`}
                    className="w-full justify-center text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  >
                    {isDeleting ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Usuwanie…</>
                    ) : (
                      <><Trash2 className="w-4 h-4 mr-2" />Usuń import</>
                    )}
                  </Button>
                </div>

                <div className="text-[11px] text-gray-400 leading-relaxed">
                  {imp.has_original
                    ? "PAX ma oryginalny plik wyciągu — pobierany bit-perfect."
                    : "PAX nie ma oryginału — pobierany CSV zawiera dane z bazy PAX dla tego importu."}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* DESKTOP: dotychczasowa tabela (hidden md:block) + akcja Pobierz. */}
      <Card className="hidden md:block border border-gray-200" data-testid="bank-history-card">
        <CardContent className="p-0">
          <div>
            <table className="w-full text-sm table-responsive" data-testid="bank-history-table">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">Data importu</th>
                  <th className="text-left px-3 py-2 font-medium">Plik</th>
                  <th className="text-left px-3 py-2 font-medium">Format</th>
                  <th className="text-right px-3 py-2 font-medium">Operacje</th>
                  <th className="text-left px-3 py-2 font-medium">Zakres dat</th>
                  <th className="text-left px-3 py-2 font-medium">Status</th>
                  <th className="text-right px-3 py-2 font-medium">Akcje</th>
                </tr>
              </thead>
              <tbody>
                {imports.map((imp) => {
                  const meta = FORMAT_META[imp.source_format] || FORMAT_META.unknown;
                  const isDownloading = downloadingId === imp._id;
                  const isDeleting = deletingId === imp._id;
                  return (
                    <tr key={imp._id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`bank-history-row-${imp._id}`}>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-700">{formatDateTime(imp.created_at)}</td>
                      <td className="px-3 py-2 max-w-[28ch] truncate text-gray-800" title={imp.file_name}>{imp.file_name}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium border ${meta.className}`}>{meta.label}</span>
                      </td>
                      <td className="px-3 py-2 text-right text-gray-700">{imp.operations_count}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-gray-600 text-xs">{imp.date_from || "—"}{imp.date_to ? ` → ${imp.date_to}` : ""}</td>
                      <td className="px-3 py-2 text-gray-600">{imp.status || "—"}</td>
                      <td className="px-3 py-2 text-right">
                        <div className="inline-flex items-center gap-1">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isDownloading}
                            onClick={() => handleDownload(imp)}
                            data-testid={`bank-history-download-${imp._id}`}
                            title={imp.has_original ? "Pobierz oryginał" : "Pobierz CSV z danych"}
                            className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 h-8 px-2"
                          >
                            {isDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isDeleting}
                            onClick={() => requestDelete(imp)}
                            data-testid={`bank-history-delete-${imp._id}`}
                            title="Usuń import"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 h-8 px-2"
                          >
                            {isDeleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="bank-history-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Usunąć import?</AlertDialogTitle>
            <AlertDialogDescription>
              Plik <strong>{confirmDelete?.file_name}</strong> zostanie usunięty
              wraz z <strong>{confirmDelete?.operations_count}</strong> operacjami.
              Tej operacji nie da się cofnąć.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bank-history-delete-cancel">Anuluj</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirmed}
              data-testid="bank-history-delete-confirm"
              className="bg-red-600 hover:bg-red-700"
            >
              Usuń import
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};


const SORT_COLUMNS = [
  { id: "booking_date", label: "Data", align: "left" },
  { id: "amount", label: "Kwota", align: "right" },
  { id: "counterparty_name", label: "Kontrahent", align: "left" },
  { id: "title", label: "Tytuł", align: "left" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

const SortHeader = ({ col, sortBy, sortDir, onSort }) => {
  const isActive = sortBy === col.id;
  const Icon = isActive ? (sortDir === "asc" ? ArrowUp : ArrowDown) : ChevronsUpDown;
  const alignClass = col.align === "right" ? "text-right justify-end" : "text-left";
  return (
    <th className={`px-3 py-2 font-medium ${alignClass}`}>
      <button
        type="button"
        onClick={() => onSort(col.id)}
        data-testid={`bank-tx-sort-${col.id}`}
        className={[
          "inline-flex items-center gap-1 hover:text-gray-900 transition-colors",
          isActive ? "text-[#0F1F3D]" : "text-gray-600",
        ].join(" ")}
      >
        <span>{col.label}</span>
        <Icon className={`w-3.5 h-3.5 ${isActive ? "opacity-100" : "opacity-40"}`} />
      </button>
    </th>
  );
};

// ────────────────────────────────────────────────────────────────────────
// TransactionDetailPanel — rozwijany szczegół transakcji (2026-02).
//
// Ekran diagnostyczny + docelowy „szczegóły operacji" dla przyszłych
// integracji (Agent Płatności, dopasowanie, audyt). Prezentuje:
//
//   1. Dane ustandaryzowane — sekcje OPERACJA / NADAWCA / ODBIORCA / OPIS
//      / IMPORT / POWIĄZANIA. Kierunek (nadawca vs odbiorca) wnioskujemy
//      z znaku kwoty: amount < 0 → wychodząca (my=nadawca, cp=odbiorca);
//      amount > 0 → przychodząca (cp=nadawca, my=odbiorca).
//   2. Dane źródłowe (RAW) — pełny dokument z bazy pod expandable
//      sekcją „▼ Dane źródłowe (RAW)".
//
// Design ready-for-extension: struktura sekcji łatwo pozwoli dołożyć:
//   • log decyzji AI (dodatkowa sekcja)
//   • historia zmian (dodatkowa sekcja)
//   • załączniki (dodatkowa sekcja)
// bez dotykania istniejących sekcji.
// ────────────────────────────────────────────────────────────────────────

const NO_DATA = "— brak danych —";

// Pola źródłowe (RAW) z których pochodzi konkretne pole ustandaryzowane.
// Widoczne pod wartością — ułatwia diagnostykę parserów różnych banków.
const FIELD_SOURCE_HINTS = {
  amount:            ["amount"],
  currency:          ["currency"],
  booking_date:      ["booking_date"],
  transaction_date:  ["transaction_date"],
  counterparty_name: ["counterparty_name"],
  counterparty_iban: ["counterparty_iban", "counterparty_account", "iban"],
  source_account:    ["source_account"],
  title:             ["title"],
  transaction_id:    ["transaction_id"],
  reference:         ["reference"],
  payment_method_hint: ["payment_method_hint"],
  source_format:     ["source_format"],
};

const CopyButton = ({ value, testId }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.stopPropagation();
    if (!value) return;
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success("Skopiowano.");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Nie udało się skopiować.");
    }
  };
  return (
    <button
      type="button"
      onClick={handleCopy}
      className="ml-1.5 inline-flex items-center text-gray-400 hover:text-indigo-600 transition-colors"
      title="Kopiuj"
      data-testid={testId}
    >
      {copied ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
    </button>
  );
};

const DetailRow = ({ label, value, copyable = false, testId, sourceKey }) => {
  const empty = value === null || value === undefined || value === "";
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-1 py-1" data-testid={testId}>
      <div className="text-xs font-medium text-gray-500 sm:pt-0.5">{label}</div>
      <div className={`text-sm ${empty ? "text-gray-400 italic" : "text-gray-900"} flex items-start flex-wrap gap-x-1`}>
        <span className="break-all">{empty ? NO_DATA : String(value)}</span>
        {!empty && copyable ? <CopyButton value={value} testId={testId ? `${testId}-copy` : undefined} /> : null}
        {!empty && sourceKey && FIELD_SOURCE_HINTS[sourceKey] ? (
          <span className="text-[10px] text-gray-400 font-mono w-full mt-0.5" data-testid={testId ? `${testId}-source` : undefined}>
            źródło: {FIELD_SOURCE_HINTS[sourceKey].join(" | ")}
          </span>
        ) : null}
      </div>
    </div>
  );
};

const Section = ({ title, testId, children }) => (
  <div className="mt-3" data-testid={testId}>
    <div className="text-[11px] font-bold uppercase tracking-wider text-gray-600 mb-1.5 border-b border-gray-200 pb-1">
      {title}
    </div>
    <div className="space-y-0">{children}</div>
  </div>
);

const RawDataViewer = ({ doc }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4 border-t border-gray-200 pt-3" data-testid="tx-detail-raw-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-gray-900"
        data-testid="tx-detail-raw-toggle"
        aria-expanded={open}
      >
        {open ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
        Dane źródłowe (RAW)
      </button>
      {open ? (
        <div className="mt-2 space-y-2" data-testid="tx-detail-raw-body">
          <div className="text-[11px] text-gray-500">
            Pełny rekord zapisany w bazie <code className="font-mono">bank_transactions._id = {doc?._id || "—"}</code>.
            Pole <code className="font-mono">raw</code> zawiera oryginalny, znormalizowany słownik z parsera —
            wszystkie pola źródłowe są tutaj bez filtracji.
          </div>
          <pre
            className="text-[11px] font-mono bg-gray-900 text-gray-100 p-3 rounded-md overflow-x-auto whitespace-pre-wrap break-words max-h-[400px] overflow-y-auto"
            data-testid="tx-detail-raw-json"
          >
            {JSON.stringify(doc, null, 2)}
          </pre>
        </div>
      ) : null}
    </div>
  );
};

const TransactionDetailPanel = ({ txId, doc, loading, error }) => {
  if (loading) {
    return (
      <div className="p-4 bg-indigo-50/40 border-t border-indigo-100 text-xs text-gray-500 flex items-center gap-2" data-testid={`tx-detail-loading-${txId}`}>
        <Loader2 className="w-4 h-4 animate-spin" /> Ładowanie szczegółów…
      </div>
    );
  }
  if (error) {
    return (
      <div className="p-4 bg-red-50 border-t border-red-200 text-xs text-red-700" data-testid={`tx-detail-error-${txId}`}>
        {error}
      </div>
    );
  }
  if (!doc) return null;

  const isOutgoing = Number(doc.amount || 0) < 0;
  const rawFields = doc.raw || {};
  // Pola niezmapowane w modelu PAX — pokazujemy je pod RAW jako listę
  // (informacja dla diagnostyki parserów, poza obecnym mapowaniem).
  const MAPPED_KEYS = new Set([
    "booking_date", "transaction_date", "amount", "currency",
    "counterparty_name", "counterparty_iban", "title", "source_account",
    "transaction_id", "reference", "source_format", "payment_method_hint",
  ]);
  const unmappedKeys = Object.keys(rawFields).filter((k) => !MAPPED_KEYS.has(k));

  return (
    <div
      className="p-4 sm:p-5 bg-indigo-50/30 border-t border-indigo-100"
      data-testid={`tx-detail-panel-${txId}`}
    >
      {/* OPERACJA */}
      <Section title="Operacja" testId="tx-detail-section-operacja">
        <DetailRow label="Typ operacji" value={isOutgoing ? "Wychodząca (obciążenie)" : "Przychodząca (uznanie)"} testId="tx-detail-typ" />
        <DetailRow label="Status" value={NO_DATA === "— brak danych —" && !doc.status ? null : doc.status} testId="tx-detail-status" />
        <DetailRow label="Kwota" value={doc.amount != null ? `${doc.amount}` : null} sourceKey="amount" testId="tx-detail-amount" />
        <DetailRow label="Waluta" value={doc.currency} sourceKey="currency" testId="tx-detail-currency" />
        <DetailRow label="Data operacji" value={doc.transaction_date} sourceKey="transaction_date" testId="tx-detail-transaction-date" />
        <DetailRow label="Data księgowania" value={doc.booking_date} sourceKey="booking_date" testId="tx-detail-booking-date" />
        <DetailRow label="Saldo po operacji" value={rawFields.balance_after || rawFields.saldo_po_operacji} testId="tx-detail-balance" />
      </Section>

      {/* NADAWCA */}
      <Section title="Nadawca" testId="tx-detail-section-nadawca">
        {isOutgoing ? (
          <>
            <DetailRow label="Nazwa nadawcy" value={rawFields.sender_name || rawFields.own_name} testId="tx-detail-sender-name" />
            <DetailRow label="Nazwa konta" value={rawFields.own_account_name} testId="tx-detail-sender-account-name" />
            <DetailRow label="Numer rachunku / IBAN" value={doc.source_account} copyable sourceKey="source_account" testId="tx-detail-sender-iban" />
            <DetailRow label="Bank" value={rawFields.own_bank_name || rawFields.sender_bank} testId="tx-detail-sender-bank" />
            <DetailRow label="Adres" value={rawFields.sender_address} testId="tx-detail-sender-address" />
            <DetailRow label="Kod pocztowy" value={rawFields.sender_postal_code} testId="tx-detail-sender-postal" />
            <DetailRow label="Miejscowość" value={rawFields.sender_city} testId="tx-detail-sender-city" />
            <DetailRow label="Kraj" value={rawFields.sender_country} testId="tx-detail-sender-country" />
          </>
        ) : (
          <>
            <DetailRow label="Nazwa nadawcy" value={doc.counterparty_name} sourceKey="counterparty_name" testId="tx-detail-sender-name" />
            <DetailRow label="Nazwa konta" value={rawFields.counterparty_account_name} testId="tx-detail-sender-account-name" />
            <DetailRow label="Numer rachunku / IBAN" value={doc.counterparty_iban} copyable sourceKey="counterparty_iban" testId="tx-detail-sender-iban" />
            <DetailRow label="Bank" value={rawFields.counterparty_bank_name || rawFields.sender_bank} testId="tx-detail-sender-bank" />
            <DetailRow label="Adres" value={rawFields.counterparty_address || rawFields.sender_address} testId="tx-detail-sender-address" />
            <DetailRow label="Kod pocztowy" value={rawFields.counterparty_postal_code || rawFields.sender_postal_code} testId="tx-detail-sender-postal" />
            <DetailRow label="Miejscowość" value={rawFields.counterparty_city || rawFields.sender_city} testId="tx-detail-sender-city" />
            <DetailRow label="Kraj" value={rawFields.counterparty_country || rawFields.sender_country} testId="tx-detail-sender-country" />
          </>
        )}
      </Section>

      {/* ODBIORCA */}
      <Section title="Odbiorca" testId="tx-detail-section-odbiorca">
        {isOutgoing ? (
          <>
            <DetailRow label="Nazwa odbiorcy" value={doc.counterparty_name} sourceKey="counterparty_name" testId="tx-detail-receiver-name" />
            <DetailRow label="Nazwa konta" value={rawFields.counterparty_account_name} testId="tx-detail-receiver-account-name" />
            <DetailRow label="Numer rachunku / IBAN" value={doc.counterparty_iban} copyable sourceKey="counterparty_iban" testId="tx-detail-receiver-iban" />
            <DetailRow label="Bank" value={rawFields.counterparty_bank_name || rawFields.receiver_bank} testId="tx-detail-receiver-bank" />
            <DetailRow label="Adres" value={rawFields.counterparty_address || rawFields.receiver_address} testId="tx-detail-receiver-address" />
            <DetailRow label="Kod pocztowy" value={rawFields.counterparty_postal_code || rawFields.receiver_postal_code} testId="tx-detail-receiver-postal" />
            <DetailRow label="Miejscowość" value={rawFields.counterparty_city || rawFields.receiver_city} testId="tx-detail-receiver-city" />
            <DetailRow label="Kraj" value={rawFields.counterparty_country || rawFields.receiver_country} testId="tx-detail-receiver-country" />
          </>
        ) : (
          <>
            <DetailRow label="Nazwa odbiorcy" value={rawFields.receiver_name || rawFields.own_name} testId="tx-detail-receiver-name" />
            <DetailRow label="Nazwa konta" value={rawFields.own_account_name} testId="tx-detail-receiver-account-name" />
            <DetailRow label="Numer rachunku / IBAN" value={doc.source_account} copyable sourceKey="source_account" testId="tx-detail-receiver-iban" />
            <DetailRow label="Bank" value={rawFields.own_bank_name || rawFields.receiver_bank} testId="tx-detail-receiver-bank" />
            <DetailRow label="Adres" value={rawFields.receiver_address} testId="tx-detail-receiver-address" />
            <DetailRow label="Kod pocztowy" value={rawFields.receiver_postal_code} testId="tx-detail-receiver-postal" />
            <DetailRow label="Miejscowość" value={rawFields.receiver_city} testId="tx-detail-receiver-city" />
            <DetailRow label="Kraj" value={rawFields.receiver_country} testId="tx-detail-receiver-country" />
          </>
        )}
      </Section>

      {/* OPIS */}
      <Section title="Opis" testId="tx-detail-section-opis">
        <DetailRow label="Tytuł przelewu" value={doc.title} copyable sourceKey="title" testId="tx-detail-title" />
        <DetailRow label="Opis operacji" value={rawFields.description || rawFields.opis_operacji} testId="tx-detail-description" />
        <DetailRow label="Referencja" value={doc.reference} copyable sourceKey="reference" testId="tx-detail-reference" />
        <DetailRow label="Numer operacji" value={doc.transaction_id} copyable sourceKey="transaction_id" testId="tx-detail-transaction-id" />
        <DetailRow label="Identyfikator bankowy" value={rawFields.bank_ref || rawFields.bank_reference} testId="tx-detail-bank-ref" />
        <DetailRow label="Typ księgowania" value={doc.payment_method_hint || rawFields.booking_type} sourceKey="payment_method_hint" testId="tx-detail-booking-type" />
      </Section>

      {/* IMPORT */}
      <Section title="Import" testId="tx-detail-section-import">
        <DetailRow label="Import ID" value={doc.import_id} copyable testId="tx-detail-import-id" />
        <DetailRow label="Data importu" value={doc.created_at} testId="tx-detail-created-at" />
        <DetailRow label="Źródło importu" value={doc.source_format} sourceKey="source_format" testId="tx-detail-source-format" />
        <DetailRow label="Numer rekordu" value={rawFields.row_number || rawFields.record_id} testId="tx-detail-record-num" />
        <DetailRow label="Bank źródłowy" value={rawFields.source_bank || rawFields.bank_name} testId="tx-detail-source-bank" />
      </Section>

      {/* POWIĄZANIA — pokazujemy WYŁĄCZNIE zapisane w doc'u pola link_* */}
      <Section title="Powiązania" testId="tx-detail-section-powiazania">
        {(doc.linked_vendor || doc.linked_invoice_id || doc.linked_package_id) ? (
          <>
            <DetailRow label="Powiązany kontrahent" value={doc.linked_vendor} testId="tx-detail-linked-vendor" />
            <DetailRow label="Powiązana faktura" value={doc.linked_invoice_id} testId="tx-detail-linked-invoice" />
            <DetailRow label="Powiązana paczka" value={doc.linked_package_id} testId="tx-detail-linked-package" />
          </>
        ) : (
          <div className="text-xs text-gray-500 italic py-1" data-testid="tx-detail-no-links">Niepowiązano</div>
        )}
      </Section>

      {/* Niezmapowane pola z RAW — jeśli parser konkretnego banku zapisał
          coś czego model PAX nie mapuje, wyświetlamy tutaj jako listę. */}
      {unmappedKeys.length > 0 ? (
        <Section title={`Niezmapowane pola źródłowe (${unmappedKeys.length})`} testId="tx-detail-section-unmapped">
          <div className="text-[11px] text-gray-500 mb-1">
            Poniższe pola są zapisane w RAW, ale nie są obecnie mapowane do modelu PAX.
            To potencjalne rozszerzenia parsera dla konkretnych banków — patrz raport diagnostyczny.
          </div>
          {unmappedKeys.map((k) => (
            <DetailRow key={k} label={k} value={rawFields[k]} testId={`tx-detail-unmapped-${k}`} />
          ))}
        </Section>
      ) : null}

      {/* RAW — pełny dokument JSON */}
      <RawDataViewer doc={doc} />
    </div>
  );
};



const TransactionsTab = ({ reloadKey }) => {
  const [txs, setTxs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1); // 1-based
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("booking_date");
  const [sortDir, setSortDir] = useState("desc");
  // 2026-02: wyszukiwarka i toggle sortowania A-Z / Z-A po kontrahencie.
  // Debounce 300ms — nie strzelamy do backendu przy każdej literze.
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(searchTerm.trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);
  // Reset paginacji gdy zmienia się search — inaczej user może utknąć na
  // stronie 5 z pustym wynikiem po zawężeniu wyszukiwania.
  useEffect(() => { setPage(1); }, [debouncedQ]);
  // 2026-02: rozwijanie pojedynczej karty transakcji + cache szczegółów.
  // Tylko jedna transakcja rozwinięta w danym momencie.
  // `detailsCache` — mapa tx_id → pełny dokument z backendu (raw + wszystkie
  // pola). Kliknięcie tej samej transakcji nie powoduje ponownego fetcha.
  const [expandedId, setExpandedId] = useState(null);
  const [detailsCache, setDetailsCache] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailError, setDetailError] = useState(null);

  const toggleExpand = useCallback(async (txId) => {
    if (!txId) return;
    setDetailError(null);
    if (expandedId === txId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(txId);
    if (detailsCache[txId]) return; // hit — nie pobieramy ponownie
    setDetailLoadingId(txId);
    try {
      const res = await axios.get(
        `${API}/bank-statements/transactions/${encodeURIComponent(txId)}`,
        { withCredentials: true },
      );
      setDetailsCache((prev) => ({ ...prev, [txId]: res.data || {} }));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setDetailError(typeof detail === "string" ? detail : "Nie udało się pobrać szczegółów transakcji.");
    } finally {
      setDetailLoadingId(null);
    }
  }, [expandedId, detailsCache]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const skip = (page - 1) * pageSize;
        const params = { skip, limit: pageSize, sort_by: sortBy, sort_dir: sortDir };
        if (debouncedQ) params.q = debouncedQ;
        const res = await axios.get(`${API}/bank-statements/transactions`, {
          params,
          withCredentials: true,
        });
        if (!cancelled) {
          setTxs(res.data?.transactions || []);
          setTotal(res.data?.total ?? 0);
        }
      } catch (e) {
        if (!cancelled) toast.error(e?.response?.data?.detail || "Błąd pobierania transakcji.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey, page, pageSize, sortBy, sortDir, debouncedQ]);

  // Klik w aktywną kolumnę toggle'uje asc/desc; klik w nieaktywną → DESC.
  // Uwaga: NIE używamy setSortDir wewnątrz updater'a setSortBy — w React StrictMode
  // updatery wywoływane są 2x, co przy toggle (asc<->desc) zerowałoby zmianę.
  const handleSort = (columnId) => {
    setPage(1);
    if (sortBy === columnId) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(columnId);
      setSortDir("desc");
    }
  };

  const handlePageSize = (val) => {
    setPage(1);
    setPageSize(Number(val));
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const fromRow = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const toRow = Math.min(page * pageSize, total);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  // Toolbar (search + sort A-Z/Z-A) — reużywalny inline JSX (bez wspólnego komponentu).
  const nameSortActive = sortBy === "counterparty_name";
  const nameSortAsc = nameSortActive && sortDir === "asc";
  const toggleNameSort = () => {
    setPage(1);
    if (!nameSortActive) {
      setSortBy("counterparty_name");
      setSortDir("asc");
    } else if (sortDir === "asc") {
      setSortDir("desc");
    } else {
      // było counterparty_name desc → wracamy do domyślnego booking_date desc
      setSortBy("booking_date");
      setSortDir("desc");
    }
  };
  const toolbar = (
    <div
      className="flex flex-col sm:flex-row gap-2 sm:items-center mb-3"
      data-testid="bank-tx-toolbar"
    >
      <div className="relative flex-1 min-w-0">
        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Szukaj po nazwie kontrahenta lub tytule…"
          data-testid="bank-tx-search-input"
          className="w-full h-9 pl-8 pr-8 rounded border border-gray-300 bg-white text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          autoComplete="off"
          spellCheck={false}
        />
        {searchTerm && (
          <button
            type="button"
            onClick={() => setSearchTerm("")}
            data-testid="bank-tx-search-clear"
            aria-label="Wyczyść wyszukiwarkę"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={toggleNameSort}
        data-testid="bank-tx-sort-toggle"
        aria-pressed={nameSortActive}
        aria-label={
          !nameSortActive
            ? "Sortuj A-Z po nazwie kontrahenta"
            : (nameSortAsc ? "Sortuj Z-A" : "Wyłącz sortowanie po nazwie")
        }
        className={`h-9 px-3 rounded border text-sm flex items-center gap-1.5 shrink-0 ${
          nameSortActive
            ? "border-indigo-300 bg-indigo-50 text-indigo-800"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
        title={
          !nameSortActive
            ? "Sortuj A-Z"
            : (nameSortAsc ? "Klik: Z-A" : "Klik: wyłącz sortowanie")
        }
      >
        {nameSortAsc || !nameSortActive
          ? <ArrowUpAZ className="w-4 h-4 text-gray-500" />
          : <ArrowDownAZ className="w-4 h-4 text-gray-500" />}
        <span>Sortuj {nameSortActive ? (nameSortAsc ? "A-Z" : "Z-A") : "A-Z"}</span>
      </button>
    </div>
  );

  if (loading && txs.length === 0) {
    return (
      <div>
        {toolbar}
        <div className="text-sm text-gray-500">
          <Loader2 className="w-4 h-4 inline animate-spin mr-2" />Ładowanie transakcji…
        </div>
      </div>
    );
  }
  if (!loading && total === 0) {
    const emptyMsg = debouncedQ
      ? `Brak transakcji pasujących do „${debouncedQ}".`
      : "Brak zapisanych transakcji.";
    return (
      <div>
        {toolbar}
        <Card className="border border-gray-200">
          <CardContent className="p-6 text-sm text-gray-500" data-testid="bank-tx-empty">
            {emptyMsg}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div>
      {toolbar}
      <Card className="border border-gray-200" data-testid="bank-tx-card">
        <CardContent className="p-0">
        {/* Mobile: karty (md:hidden) */}
        <div className="md:hidden space-y-2.5 p-3" data-testid="bank-tx-mobile-list">
          {txs.map((tx, idx) => {
            const isExpanded = expandedId === tx._id;
            return (
            <div
              key={tx._id || idx}
              className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden"
              data-testid={`bank-tx-mobile-card-${idx}`}
            >
              <button
                type="button"
                onClick={() => toggleExpand(tx._id)}
                className="w-full text-left p-3 hover:bg-gray-50 transition-colors"
                data-testid={`bank-tx-mobile-toggle-${idx}`}
                aria-expanded={isExpanded}
              >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 truncate" title={tx.counterparty_name || ""}>
                    {tx.counterparty_name || "—"}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {tx.booking_date || "—"}
                  </div>
                </div>
                <div
                  className={`shrink-0 font-mono text-sm tabular-nums ${tx.amount < 0 ? "text-red-700" : "text-emerald-700"}`}
                >
                  {formatAmount(tx.amount, tx.currency)}
                </div>
              </div>
              <div
                className="text-xs text-gray-700 mt-1.5 line-clamp-2 break-words"
                title={tx.title || ""}
              >
                {tx.title || "—"}
              </div>
              {tx.counterparty_iban && (
                <div
                  className="text-[10px] text-gray-400 font-mono mt-1 truncate"
                  title={tx.counterparty_iban}
                >
                  {tx.counterparty_iban}
                </div>
              )}
              </button>
              {isExpanded ? (
                <TransactionDetailPanel
                  txId={tx._id}
                  doc={detailsCache[tx._id]}
                  loading={detailLoadingId === tx._id}
                  error={detailError}
                />
              ) : null}
            </div>
            );
          })}
        </div>

        {/* Desktop: tabela (hidden md:block). Pakiet 1: bez overflow-x-auto —
            szeroki kontener + adaptacyjne kolumny (Rachunek chowamy na xl:). */}
        <div className="hidden md:block rounded-md border border-gray-200">
          <table className="w-full text-sm table-responsive" data-testid="bank-tx-table">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                {SORT_COLUMNS.map((col) => (
                  <SortHeader key={col.id} col={col} sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                ))}
                <th className="px-3 py-2 font-medium text-left">Rachunek</th>
              </tr>
            </thead>
            <tbody>
              {txs.map((tx, idx) => {
                const isExpanded = expandedId === tx._id;
                return (
                <Fragment key={tx._id || idx}>
                <tr
                  className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${isExpanded ? "bg-indigo-50/40" : ""}`}
                  data-testid={`bank-tx-row-${idx}`}
                  onClick={() => toggleExpand(tx._id)}
                  aria-expanded={isExpanded}
                >
                  <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{tx.booking_date || "—"}</td>
                  <td className={`px-3 py-2 text-right whitespace-nowrap font-mono tabular-nums ${tx.amount < 0 ? "text-red-700" : "text-emerald-700"}`}>{formatAmount(tx.amount, tx.currency)}</td>
                  <td className="px-3 py-2 text-gray-800 max-w-[28ch] truncate" title={tx.counterparty_name || ""}>{tx.counterparty_name || "—"}</td>
                  <td className="px-3 py-2 text-gray-700 max-w-[36ch] truncate" title={tx.title || ""}>{tx.title || "—"}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs max-w-[24ch] truncate" title={tx.counterparty_iban || ""}>{tx.counterparty_iban || "—"}</td>
                </tr>
                {isExpanded ? (
                  <tr data-testid={`bank-tx-detail-row-${idx}`}>
                    <td colSpan={5} className="p-0">
                      <TransactionDetailPanel
                        txId={tx._id}
                        doc={detailsCache[tx._id]}
                        loading={detailLoadingId === tx._id}
                        error={detailError}
                      />
                    </td>
                  </tr>
                ) : null}
                </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination footer */}
        <div className="flex flex-wrap items-center justify-between gap-3 px-3 py-2.5 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <span data-testid="bank-tx-pagination-info">
              Wyświetlono <strong className="text-gray-800">{fromRow}–{toRow}</strong> z <strong className="text-gray-800">{total}</strong> transakcji
            </span>
            {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-1.5">
              <span className="text-gray-500">Na stronę:</span>
              <select
                value={pageSize}
                onChange={(e) => handlePageSize(e.target.value)}
                data-testid="bank-tx-page-size"
                className="h-7 rounded border border-gray-300 bg-white px-1.5 text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-[#0F1F3D]"
              >
                {PAGE_SIZE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </label>
            <div className="flex items-center gap-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                data-testid="bank-tx-page-prev"
                className="h-7 px-2"
              >
                <ChevronLeft className="w-3.5 h-3.5 mr-1" /> Poprzednia
              </Button>
              <span className="px-2 text-gray-500 tabular-nums">{page}/{totalPages}</span>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                data-testid="bank-tx-page-next"
                className="h-7 px-2"
              >
                Następna <ChevronRight className="w-3.5 h-3.5 ml-1" />
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
    </div>
  );
};


// ─── Page wrapper ────────────────────────────────────────────────────────────

const TABS = [
  { id: "upload", label: "Wgraj", icon: Upload },
  { id: "history", label: "Historia importów", icon: History },
  { id: "transactions", label: "Transakcje", icon: ListOrdered },
];

/**
 * BankAgentCta — kafel/komunikat na dole strony Wyciągi.
 *
 * - Gdy user NIE ma importów: pokazuje subtelny komunikat "Najpierw wgraj wyciąg bankowy".
 * - Gdy user MA przynajmniej 1 import: pokazuje kafel "Analiza AI wyciągów" z przyciskiem
 *   prowadzącym do /wyciagi/agent.
 *
 * `reloadKey` jest przekazywane żeby kafel odświeżał się po dodaniu/usunięciu importu.
 */
const BankAgentCta = ({ reloadKey }) => {
  const navigate = useNavigate();
  const [hasImports, setHasImports] = useState(null); // null = loading

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/bank-statements/imports?limit=1`, { withCredentials: true });
        if (!cancelled) setHasImports(Number(res.data?.total || 0) > 0);
      } catch {
        if (!cancelled) setHasImports(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (hasImports === null) return null;

  if (!hasImports) {
    return (
      <Card className="mt-6 border-dashed border-gray-300 bg-gray-50" data-testid="bank-agent-cta-empty">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="p-1.5 bg-white rounded-md border border-gray-200">
            <Bot className="w-4 h-4 text-gray-500" />
          </div>
          <div className="text-sm">
            <p className="font-medium text-gray-800">Najpierw wgraj wyciąg bankowy.</p>
            <p className="text-gray-600 mt-0.5 text-xs">
              Agent Wyciągów analizuje zapisane transakcje. Po zaimportowaniu pierwszego wyciągu
              pojawi się możliwość uruchomienia analizy AI.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mt-6 border-indigo-200 bg-indigo-50/40" data-testid="bank-agent-cta">
      <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="p-2 bg-white rounded-md border border-indigo-200 shrink-0">
          <Bot className="w-5 h-5 text-indigo-700" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-900 text-sm">Analiza AI wyciągów</p>
          <p className="text-xs text-gray-600 mt-0.5">
            Utwórz profile płatnicze kontrahentów na podstawie historii transakcji bankowych.
          </p>
        </div>
        <Button
          onClick={() => navigate("/wyciagi/agent")}
          data-testid="bank-agent-open-btn"
          className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
        >
          Otwórz Agenta Wyciągów
          <ArrowRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
};

/**
 * WyciagiStageBadge — dynamiczny badge wskazujący następny krok w workflow.
 *
 * Wylicza etap na podstawie istniejących endpointów (BEZ zmian backendu):
 *   - GET /api/bank-statements/imports?limit=1  → czy user ma jakikolwiek import?
 *   - GET /api/ai/agents/bank/counterparties     → czy istnieje przynajmniej
 *     1 profil kontrahenta (profile_status ∈ {analyzed, needs_review})?
 *
 * Mapowanie:
 *   Brak importów                     → "Krok 1: Wgraj wyciąg"
 *   Importy, brak profili             → "Krok 2: Uruchom Agenta Wyciągów"
 *   Profile istnieją                  → "Krok 3: Przejdź do Kontrahentów"
 *
 * Zachowuje styl wizualny (amber pill) — zmienia się wyłącznie treść.
 */
const WyciagiStageBadge = ({ reloadKey }) => {
  const [label, setLabel] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const importsRes = await axios.get(
          `${API}/bank-statements/imports?limit=1`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const hasImports = Number(importsRes.data?.total || 0) > 0;
        if (!hasImports) {
          setLabel("Krok 1: Wgraj wyciąg");
          return;
        }
        const cpRes = await axios.get(
          `${API}/ai/agents/bank/counterparties`,
          { withCredentials: true }
        );
        if (cancelled) return;
        const items = cpRes.data?.counterparties || [];
        const hasAnyProfile = items.some(
          (c) => c.profile_status === "analyzed" || c.profile_status === "needs_review"
        );
        setLabel(hasAnyProfile ? "Krok 3: Przejdź do Kontrahentów" : "Krok 2: Uruchom Agenta Wyciągów");
      } catch {
        // W razie błędu — fallback do pierwszego kroku (najmniej myląca treść).
        if (!cancelled) setLabel("Krok 1: Wgraj wyciąg");
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  if (!label) return null;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
      data-testid="wyciagi-stage-badge"
    >
      {label}
    </span>
  );
};


const BankStatementsPage = () => {
  // Pakiet 1: strona gęsta danymi — używamy szerszego kontenera.
  usePageContainer("wide");
  const [activeTab, setActiveTab] = useState("upload");
  const [reloadKey, setReloadKey] = useState(0);

  const handleSaved = useCallback(() => {
    setReloadKey((k) => k + 1);
  }, []);

  const switchToTransactions = useCallback(() => {
    setActiveTab("transactions");
  }, []);

  return (
    <div className="space-y-6" data-testid="bank-statements-page">
      <header className="flex items-center gap-3 flex-wrap">
        <h1 className="text-2xl sm:text-3xl font-semibold text-gray-900">Wyciągi</h1>
        <WyciagiStageBadge reloadKey={reloadKey} />
      </header>

      <div className="flex gap-1 border-b border-gray-200" role="tablist">
        {TABS.map((t) => {
          const Icon = t.icon;
          const isActive = activeTab === t.id;
          return (
            <button
              key={t.id}
              role="tab"
              type="button"
              data-testid={`tab-${t.id}`}
              onClick={() => setActiveTab(t.id)}
              className={[
                "inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
                isActive ? "border-[#0F1F3D] text-[#0F1F3D]" : "border-transparent text-gray-500 hover:text-gray-700",
              ].join(" ")}
            >
              <Icon className="w-4 h-4" />
              {t.label}
            </button>
          );
        })}
      </div>

      {activeTab === "upload" && <UploadTab onSaved={handleSaved} switchToTransactions={switchToTransactions} />}
      {activeTab === "history" && <HistoryTab reloadKey={reloadKey} onChanged={handleSaved} />}
      {activeTab === "transactions" && <TransactionsTab reloadKey={reloadKey} />}

      {/* CTA do Agenta Wyciągów — widoczne tylko gdy user ma już importy */}
      <BankAgentCta reloadKey={reloadKey} />
    </div>
  );
};

export default BankStatementsPage;
